import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  Fade,
  Stack,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  LocalFireDepartment as FireIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import {
  getDueCards,
  reviewFlashcard,
  getStreakInfo,
  getFlashcardStats,
  getUserFlashcards,
  deleteMultipleFlashcards,
  createFlashcardsFromAI
} from '../services/spacedRepetitionService';
import { trackAIQueryUsage } from '../services/subscriptionService';
import { getAllLearningHubs } from '../services/learningHubService';
import { getAllLibraryItems, loadPDFData } from '../services/libraryService';
import * as pdfjsLib from 'pdfjs-dist';

/**
 * Flashcard Review Component
 *
 * Main interface for reviewing flashcards using spaced repetition.
 * Shows one card at a time with quality-based rating.
 */
function FlashcardReview({ open, userId, userEmail, subscription, onClose, fullScreen = false, dialogProps = {} }) {
  // Review mode state
  const [dueCards, setDueCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [streakInfo, setStreakInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [sessionStats, setSessionStats] = useState({
    reviewed: 0,
    correct: 0,
    incorrect: 0
  });

  // Manage mode state (v11.12.10)
  const [mode, setMode] = useState('review'); // 'review' or 'manage'
  const [allHubs, setAllHubs] = useState([]);
  const [allPdfs, setAllPdfs] = useState([]);
  const [selectedHub, setSelectedHub] = useState('');
  const [selectedPdf, setSelectedPdf] = useState('');
  const [allFlashcards, setAllFlashcards] = useState([]);
  const [selectedFlashcards, setSelectedFlashcards] = useState([]);
  const [creating, setCreating] = useState(false);

  // Load due cards on mount
  useEffect(() => {
    if (open && userId) {
      loadDueCards();
      loadStreakInfo();
      loadStats();
    }
  }, [open, userId]);

  const loadDueCards = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const cards = await getDueCards(userId, 20);
      setDueCards(cards);

      if (cards.length === 0) {
        // No cards due
        console.log('🎉 No cards due for review!');
      }
    } catch (error) {
      console.error('Error loading due cards:', error);
      alert('Failed to load flashcards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStreakInfo = async () => {
    if (!userId) return;

    try {
      const streak = await getStreakInfo(userId);
      setStreakInfo(streak);
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadStats = async () => {
    if (!userId) return;

    try {
      const flashcardStats = await getFlashcardStats(userId);
      setStats(flashcardStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // v11.12.10: Manage mode functions
  const loadHubs = async () => {
    if (!userId) return;

    try {
      const hubs = await getAllLearningHubs(userId);
      setAllHubs(hubs || []);
    } catch (error) {
      console.error('Error loading hubs:', error);
    }
  };

  const loadPdfs = async () => {
    if (!userId) return;

    try {
      const pdfs = await getAllLibraryItems();
      setAllPdfs(pdfs || []);
    } catch (error) {
      console.error('Error loading PDFs:', error);
    }
  };

  const loadFlashcards = async () => {
    if (!userId) return;

    try {
      const flashcards = await getUserFlashcards(userId);
      setAllFlashcards(flashcards || []);
    } catch (error) {
      console.error('Error loading flashcards:', error);
    }
  };

  // Load data when switching to manage mode
  useEffect(() => {
    if (mode === 'manage' && userId) {
      loadHubs();
      loadPdfs();
      loadFlashcards();
    }
  }, [mode, userId]);

  // Filter PDFs by selected hub
  const filteredPdfs = selectedHub
    ? allPdfs.filter(pdf => {
        const hub = allHubs.find(h => h.id === selectedHub);
        return hub && hub.pdfIds && hub.pdfIds.includes(pdf.id);
      })
    : allPdfs;

  // Filter flashcards by selected hub/PDF
  const filteredFlashcards = allFlashcards.filter(card => {
    if (selectedHub && card.hubId !== selectedHub) return false;
    if (selectedPdf && card.pdfId !== selectedPdf) return false;
    return true;
  });

  const handleCreateFlashcards = async () => {
    if (!selectedPdf || creating || !userId) return;

    const pdf = allPdfs.find(p => p.id === selectedPdf);
    if (!pdf) {
      alert('Please select a PDF first');
      return;
    }

    // 🔒 Check free-tier query limit
    if (subscription?.isFree) {
      const usageCheck = await trackAIQueryUsage(userId, userEmail);
      if (!usageCheck.allowed) {
        alert(usageCheck.message || 'Daily AI query limit reached. Upgrade to Student plan for unlimited flashcards!');
        return;
      }
      if (subscription.refreshUsage) subscription.refreshUsage();
    }

    setCreating(true);

    try {
      // Load PDF data
      const pdfData = await loadPDFData(selectedPdf);
      if (!pdfData) {
        alert('Failed to load PDF data. Please try opening the PDF first.');
        setCreating(false);
        return;
      }

      // Load PDF document using PDF.js
      const loadingTask = pdfjsLib.getDocument({ data: pdfData });
      const pdfDoc = await loadingTask.promise;

      // Extract text from first 5 pages (to keep it manageable)
      const maxPages = Math.min(5, pdfDoc.numPages);
      let extractedText = '';

      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        extractedText += pageText + '\n\n';
      }

      if (extractedText.trim().length < 100) {
        alert('Not enough text content found in PDF to generate flashcards.');
        setCreating(false);
        return;
      }

      // Get hub context if selected
      const hub = selectedHub ? allHubs.find(h => h.id === selectedHub) : null;

      // Generate flashcards
      const context = {
        hubId: hub?.id || null,
        hubName: hub?.name || null,
        pdfId: pdf.id,
        pdfName: pdf.name
      };

      const chapter = pdf.chapter || pdf.name;
      const subject = pdf.subject || 'General';

      const createdCards = await createFlashcardsFromAI(
        userId,
        extractedText,
        chapter,
        subject,
        context
      );

      alert(`✅ Successfully created ${createdCards.length} flashcards from "${pdf.name}"!`);

      // Reload flashcards and stats
      await loadFlashcards();
      await loadStats();

    } catch (error) {
      console.error('Error creating flashcards:', error);
      alert('Failed to create flashcards: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedFlashcards.length === filteredFlashcards.length) {
      setSelectedFlashcards([]);
    } else {
      setSelectedFlashcards(filteredFlashcards.map(card => card.id));
    }
  };

  const handleToggleCard = (cardId) => {
    setSelectedFlashcards(prev =>
      prev.includes(cardId)
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedFlashcards.length === 0) return;

    const confirm = window.confirm(
      `Are you sure you want to delete ${selectedFlashcards.length} flashcard(s)? This cannot be undone.`
    );

    if (!confirm) return;

    try {
      await deleteMultipleFlashcards(selectedFlashcards);
      alert(`Deleted ${selectedFlashcards.length} flashcard(s) successfully!`);
      setSelectedFlashcards([]);
      loadFlashcards(); // Reload the list
      loadStats(); // Update stats
    } catch (error) {
      console.error('Error deleting flashcards:', error);
      alert('Failed to delete flashcards. Please try again.');
    }
  };

  const handleRate = async (quality) => {
    if (reviewing || dueCards.length === 0) return;

    setReviewing(true);
    const currentCard = dueCards[currentCardIndex];

    try {
      const result = await reviewFlashcard(currentCard.id, quality);

      // Update session stats
      setSessionStats(prev => ({
        reviewed: prev.reviewed + 1,
        correct: result.wasCorrect ? prev.correct + 1 : prev.correct,
        incorrect: !result.wasCorrect ? prev.incorrect + 1 : prev.incorrect
      }));

      // Show feedback briefly
      setTimeout(() => {
        // Move to next card or finish
        if (currentCardIndex < dueCards.length - 1) {
          setCurrentCardIndex(currentCardIndex + 1);
          setShowAnswer(false);
        } else {
          // Session complete
          finishSession();
        }
        setReviewing(false);
      }, 500);

    } catch (error) {
      console.error('Error reviewing card:', error);
      alert('Failed to review card. Please try again.');
      setReviewing(false);
    }
  };

  const finishSession = () => {
    // Reload streak and stats
    loadStreakInfo();
    loadStats();

    // Show completion message
    alert(`🎉 Review session complete!

Reviewed: ${sessionStats.reviewed + 1} cards
Correct: ${sessionStats.correct + (showAnswer ? 1 : 0)}
Accuracy: ${Math.round(((sessionStats.correct + 1) / (sessionStats.reviewed + 1)) * 100)}%`);

    // Close or refresh
    if (onClose) {
      onClose();
    } else {
      // Reload cards
      loadDueCards();
      setCurrentCardIndex(0);
      setShowAnswer(false);
      setSessionStats({ reviewed: 0, correct: 0, incorrect: 0 });
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  const handleSkip = () => {
    if (currentCardIndex < dueCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setShowAnswer(false);
    }
  };

  const currentCard = dueCards[currentCardIndex];
  const progress = dueCards.length > 0 ? ((currentCardIndex + 1) / dueCards.length) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullScreen ? false : "md"}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: { minHeight: fullScreen ? '100%' : '70vh', maxHeight: fullScreen ? '100%' : '90vh', height: fullScreen ? '100%' : 'auto' }
      }}
      {...dialogProps}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Mode Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={mode}
            onChange={(e, newMode) => setMode(newMode)}
            sx={{ px: 2 }}
          >
            <Tab label="Review" value="review" />
            <Tab label="Manage" value="manage" />
          </Tabs>
        </Box>

        {loading && mode === 'review' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <Typography variant="h6" color="text.secondary">
              Loading flashcards...
            </Typography>
          </Box>
        )}

        {/* Review Mode */}
        {mode === 'review' && !loading && dueCards.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              All caught up! 🎉
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              No flashcards due for review right now.
              {stats && stats.newCards > 0 && (
                <>
                  <br />
                  You have {stats.newCards} new cards to learn.
                </>
              )}
            </Typography>

            {/* Streak Info */}
            {streakInfo && streakInfo.currentStreak > 0 && (
              <Chip
                icon={<FireIcon />}
                label={`${streakInfo.currentStreak} day streak!`}
                color="warning"
                size="large"
                sx={{ fontSize: '1.1rem', py: 3 }}
              />
            )}

            <Box sx={{ mt: 4 }}>
              <Button variant="outlined" onClick={onClose || loadDueCards} startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Box>
          </Box>
        )}

        {mode === 'review' && !loading && dueCards.length > 0 && (
          <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            📚 Flashcard Review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Card {currentCardIndex + 1} of {dueCards.length}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Streak */}
          {streakInfo && streakInfo.currentStreak > 0 && (
            <Tooltip title={`${streakInfo.longestStreak} day longest streak`}>
              <Chip
                icon={<FireIcon />}
                label={`${streakInfo.currentStreak} 🔥`}
                color="warning"
                size="small"
              />
            </Tooltip>
          )}

          {/* Close button */}
          {onClose && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ mb: 4, height: 8, borderRadius: 4 }}
      />

      {/* Session Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
        <Chip
          label={`Reviewed: ${sessionStats.reviewed}`}
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<CheckIcon />}
          label={`Correct: ${sessionStats.correct}`}
          color="success"
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<CancelIcon />}
          label={`Incorrect: ${sessionStats.incorrect}`}
          color="error"
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Flashcard */}
      <Paper elevation={6} sx={{ minHeight: 400, p: 4, position: 'relative', bgcolor: 'background.paper' }}>
        {/* Card metadata */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip label={currentCard.chapter} size="small" color="primary" variant="outlined" />
          {currentCard.subject && (
            <Chip label={currentCard.subject} size="small" variant="outlined" />
          )}
          {currentCard.source === 'ai_generated' && (
            <Chip label="AI Generated" size="small" color="secondary" variant="outlined" />
          )}
        </Box>

        {/* Question (Front) */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Question:
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
            {currentCard.front}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Answer (Back) */}
        {showAnswer ? (
          <Fade in={showAnswer} timeout={300}>
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                Answer:
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.6, color: 'primary.main' }}>
                {currentCard.back}
              </Typography>

              {/* Rating buttons */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  How well did you know this?
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    size="large"
                    onClick={() => handleRate(0)}
                    disabled={reviewing}
                    startIcon={<CancelIcon />}
                  >
                    ❌ Forgot Completely
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    size="large"
                    onClick={() => handleRate(3)}
                    disabled={reviewing}
                  >
                    😐 Hard (Struggled)
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    fullWidth
                    size="large"
                    onClick={() => handleRate(4)}
                    disabled={reviewing}
                  >
                    🙂 Good (Some hesitation)
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    onClick={() => handleRate(5)}
                    disabled={reviewing}
                    startIcon={<CheckIcon />}
                  >
                    ✅ Easy (Perfect recall!)
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Fade>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleShowAnswer}
              sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
            >
              Show Answer
            </Button>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={handleSkip}
                color="inherit"
              >
                Skip this card
              </Button>
            </Box>
          </Box>
        )}

        {/* Card stats (small text at bottom) */}
        <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16 }}>
          <Typography variant="caption" color="text.secondary">
            Reviewed {currentCard.totalReviews} times •
            {currentCard.correctCount > 0 && ` ${Math.round((currentCard.correctCount / currentCard.totalReviews) * 100)}% accuracy •`}
            {currentCard.correctStreak > 0 && ` ${currentCard.correctStreak} correct streak 🔥`}
          </Typography>
        </Box>
      </Paper>

      {/* Helper text */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        💡 Tip: Be honest with your rating. The algorithm works best when you accurately assess your knowledge.
      </Typography>
          </Box>
        )}

        {/* Manage Mode */}
        {mode === 'manage' && (
          <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  🎴 Manage Flashcards
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create, view, and delete flashcards
                </Typography>
              </Box>
              {onClose && (
                <IconButton onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>

            {/* Filters */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Hub (optional)</InputLabel>
                <Select
                  value={selectedHub}
                  onChange={(e) => {
                    setSelectedHub(e.target.value);
                    setSelectedPdf(''); // Reset PDF selection when hub changes
                  }}
                  label="Hub (optional)"
                >
                  <MenuItem value="">
                    <em>All Hubs</em>
                  </MenuItem>
                  {allHubs.map(hub => (
                    <MenuItem key={hub.id} value={hub.id}>
                      {hub.icon} {hub.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>PDF (optional)</InputLabel>
                <Select
                  value={selectedPdf}
                  onChange={(e) => setSelectedPdf(e.target.value)}
                  label="PDF (optional)"
                >
                  <MenuItem value="">
                    <em>All PDFs</em>
                  </MenuItem>
                  {filteredPdfs.map(pdf => (
                    <MenuItem key={pdf.id} value={pdf.id}>
                      📄 {pdf.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                onClick={handleCreateFlashcards}
                disabled={!selectedPdf || creating}
              >
                {creating ? 'Creating Flashcards...' : 'Create Flashcards'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleSelectAll}
                disabled={filteredFlashcards.length === 0}
              >
                {selectedFlashcards.length === filteredFlashcards.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteSelected}
                disabled={selectedFlashcards.length === 0}
              >
                Delete ({selectedFlashcards.length})
              </Button>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Flashcard List */}
            <Typography variant="h6" gutterBottom>
              Flashcards ({filteredFlashcards.length})
            </Typography>

            {filteredFlashcards.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SchoolIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No flashcards found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPdf
                    ? 'Create flashcards from the selected PDF'
                    : 'Select a PDF and create flashcards'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {filteredFlashcards.map(card => (
                  <ListItem key={card.id} disablePadding>
                    <ListItemButton onClick={() => handleToggleCard(card.id)} dense>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedFlashcards.includes(card.id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={card.front}
                        secondary={
                          <>
                            {card.back && `${card.back.substring(0, 50)}${card.back.length > 50 ? '...' : ''}`}
                            {card.hubName && ` • Hub: ${card.hubName}`}
                            {card.pdfName && ` • PDF: ${card.pdfName}`}
                            {card.subject && ` • ${card.subject}`}
                          </>
                        }
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {/* Stats */}
            {stats && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Your Statistics
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Chip label={`Total: ${stats.totalCards}`} size="small" />
                  <Chip label={`Due Today: ${stats.dueToday}`} color="primary" size="small" />
                  <Chip label={`Mastered: ${stats.masteredCards}`} color="success" size="small" />
                  <Chip label={`Learning: ${stats.learningCards}`} color="warning" size="small" />
                  <Chip label={`Accuracy: ${stats.reviewAccuracy}%`} color="info" size="small" />
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      {/* Footer */}
      <Box sx={{ 
        py: 1, 
        px: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        textAlign: 'center',
        bgcolor: 'grey.50'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.4 }}>
          © 2026 Ekamanam AI Innovations | PDF copyrights belong to respective owners | For personal educational use only
        </Typography>
      </Box>
    </Dialog>
  );
}

export default FlashcardReview;
